import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  DatabaseZap, ArrowRight, ArrowLeftRight, PlayCircle, Search,
  Loader2, CheckCircle2, XCircle, AlertTriangle, ServerCog, SearchCheck, BookOpen, Trash2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SUBJECT_LABELS = { math: "Math", science: "Science", english: "English Grammar", hindi: "Hindi Vyakaran" };
const BOARD_LABELS = { cbse: "CBSE", ib: "IB" };

function scopeLabel(board, grade, subject) {
  const subj = SUBJECT_LABELS[subject] || "All subjects";
  const cls = grade ? `Class ${grade}` : "All classes";
  const brd = BOARD_LABELS[board] || "All boards";
  return `${subj} · ${cls} (${brd})`;
}

// Reusable Board / Class / Subject pickers.
function ScopePicker({ board, grade, subject, setBoard, setGrade, setSubject, testidPrefix, onChange }) {
  const wrap = (setter) => (v) => { setter(v); if (onChange) onChange(); };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">Board</p>
        <select value={board} onChange={(e) => wrap(setBoard)(e.target.value)} data-testid={`${testidPrefix}-board`}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">All boards</option>
          <option value="cbse">CBSE</option>
          <option value="ib">IB</option>
        </select>
      </div>
      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">Class</p>
        <select value={grade} onChange={(e) => wrap(setGrade)(e.target.value)} data-testid={`${testidPrefix}-grade`}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">All classes</option>
          {[5, 6, 7, 8].map((g) => <option key={g} value={g}>Class {g}</option>)}
        </select>
      </div>
      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">Subject</p>
        <select value={subject} onChange={(e) => wrap(setSubject)(e.target.value)} data-testid={`${testidPrefix}-subject`}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">All subjects</option>
          <option value="math">Math</option>
          <option value="science">Science</option>
          <option value="english">English Grammar</option>
          <option value="hindi">Hindi Vyakaran</option>
        </select>
      </div>
    </div>
  );
}

// ============ PRIMARY: Promote content to Production ============
function PromoteCard({ readOnly = false }) {
  const [board, setBoard] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [diffRows, setDiffRows] = useState(null);
  const [checking, setChecking] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushJob, setPushJob] = useState(null);
  const pushPoll = useRef(null);

  const scopeBody = () => ({ direction: "push", content_scope: { board, grade, subject } });
  const resetResults = () => { setDiffRows(null); setPushJob(null); };

  const doDiff = async () => {
    setChecking(true);
    setDiffRows(null);
    setPushJob(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/diff`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(scopeBody()),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || "Review failed");
      setDiffRows(data.rows);
      const missing = data.rows.reduce((s, r) => s + (r.missing_count || 0), 0);
      toast[missing ? "info" : "success"](missing
        ? `${missing} new record(s) will be added in Production for this scope`
        : "Production is already up to date for this scope");
    } catch (e) {
      toast.error(e.message || "Review failed");
    }
    setChecking(false);
  };

  const pollPush = (jobId) => {
    if (pushPoll.current) clearInterval(pushPoll.current);
    pushPoll.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/admin/migration/status/${jobId}`, { credentials: "include" });
        const data = await res.json();
        if (data.success) {
          setPushJob(data.job);
          if (data.job.status === "completed" || data.job.status === "failed") {
            clearInterval(pushPoll.current);
            setPushing(false);
            if (data.job.status === "completed") toast.success("Content promoted to Production ✔");
            else toast.error("Promotion failed: " + (data.job.error || "unknown"));
          }
        }
      } catch { /* keep polling */ }
    }, 1500);
  };

  const doPush = async () => {
    const label = scopeLabel(board, grade, subject);
    if (!window.confirm(`Promote content to PRODUCTION?\n\nScope: ${label}\nThis adds new and updates changed records. It never deletes anything.\n\nTip: use "Review what will change" first to preview.`)) return;
    setPushing(true);
    setPushJob(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/start`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ ...scopeBody(), strategy: "merge" }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || "Promotion failed to start");
      setPushJob({ status: "running", processed: 0, total: 0 });
      pollPush(data.job_id);
      toast.info("Promoting to Production in the background…");
    } catch (e) {
      toast.error(e.message || "Promotion failed to start");
      setPushing(false);
    }
  };

  useEffect(() => () => { if (pushPoll.current) clearInterval(pushPoll.current); }, []);

  // Plain result summary from the completed job's per-collection counts.
  const resultSummary = () => {
    if (!pushJob || pushJob.status !== "completed" || !pushJob.collections) return null;
    let inserted = 0, updated = 0;
    Object.values(pushJob.collections).forEach((v) => {
      inserted += v.inserted || 0; updated += v.updated || 0;
    });
    return `${scopeLabel(board, grade, subject)}: ${inserted} new, ${updated} updated`;
  };

  return (
    <Card className="bg-white border-emerald-200" data-testid="content-sync-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-emerald-600" /> Promote content to Production
        </CardTitle>
        <CardDescription>
          Pick what to promote, review what will change, then promote. Only adds new and updates changed
          curriculum content (topics, sub-concepts, practice questions, textbooks) — never deletes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScopePicker
          board={board} grade={grade} subject={subject}
          setBoard={setBoard} setGrade={setGrade} setSubject={setSubject}
          testidPrefix="content-sync" onChange={resetResults}
        />

        <div className="flex gap-2 flex-wrap">
          <Button onClick={doDiff} disabled={readOnly || checking || pushing} variant="outline"
            className="border-gray-300 text-gray-700" data-testid="content-sync-diff-btn">
            {checking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SearchCheck className="h-4 w-4 mr-2" />}
            Review what will change
          </Button>
          <Button onClick={doPush} disabled={readOnly || pushing || checking}
            className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="content-sync-push-btn">
            {pushing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
            Promote to Production
          </Button>
        </div>

        {diffRows && (
          <div className="rounded-lg border border-gray-200 overflow-hidden" data-testid="content-sync-diff-result">
            {diffRows.map((r) => (
              <div key={r.collection} className="flex items-center justify-between px-3 py-2 text-sm even:bg-gray-50">
                <span className="font-medium text-gray-700">{r.collection}</span>
                {r.error ? <span className="text-red-600 text-xs">{r.error}</span> : (
                  <span className="text-gray-600">
                    preview {r.source_count} · production {r.target_count} ·
                    <span className={r.missing_count ? "text-emerald-700 font-semibold" : "text-gray-400"}> {r.missing_count} new</span>
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {pushJob && (
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="content-sync-push-status">
            <p className="font-medium text-emerald-800 flex items-center gap-1.5">
              {pushJob.status === "completed" && <CheckCircle2 className="h-4 w-4" />}
              {pushJob.status === "failed" && <XCircle className="h-4 w-4 text-red-500" />}
              {(pushJob.status === "running" || pushJob.status === "queued") && <Loader2 className="h-4 w-4 animate-spin" />}
              {pushJob.status === "completed" ? "Promotion complete" : pushJob.status === "failed" ? "Promotion failed" : "Promoting to Production…"}
              {pushJob.total ? ` — ${pushJob.processed || 0}/${pushJob.total}` : ""}
            </p>
            {resultSummary() && <p className="text-emerald-900 mt-1 font-medium" data-testid="content-sync-result-summary">{resultSummary()}</p>}
            {pushJob.error && <p className="text-red-600 text-xs mt-1">{pushJob.error}</p>}
            <p className="text-xs text-emerald-700/70 mt-1">This promotion is recorded in the Audit Logs tab.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ DANGER ZONE: destructive expert actions ============
function DangerZone({ readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [dbs, setDbs] = useState([]);
  const [target, setTarget] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const loadDbs = async () => {
    try {
      const res = await fetch(`${API}/api/admin/databases/list`, { credentials: "include" });
      const data = await res.json();
      if (data.success) setDbs((data.databases || []).filter((d) => d.can_delete));
    } catch (e) { /* ignore */ }
  };

  const toggle = () => { const n = !open; setOpen(n); if (n) loadDbs(); };

  const drop = async () => {
    if (!target) return toast.error("Choose a database");
    const expected = `DELETE_${target.toUpperCase()}`;
    if (confirm !== expected) return toast.error(`Type exactly: ${expected}`);
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/databases/${encodeURIComponent(target)}?confirm=${encodeURIComponent(confirm)}`, {
        method: "DELETE", credentials: "include",
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Dropped '${target}' (${data.collections_removed} collections)`);
        setTarget(""); setConfirm(""); loadDbs();
      } else {
        toast.error(data.detail || "Drop failed");
      }
    } catch (e) {
      toast.error("Drop failed");
    } finally { setBusy(false); }
  };

  // ---- Mirror / Replace (deletes prod extras) — expert only ----
  const [mBoard, setMBoard] = useState("");
  const [mGrade, setMGrade] = useState("");
  const [mSubject, setMSubject] = useState("");
  const [mPrev, setMPrev] = useState(null);
  const [mChecking, setMChecking] = useState(false);
  const [mConfirm, setMConfirm] = useState("");
  const [mRunning, setMRunning] = useState(false);
  const [mJob, setMJob] = useState(null);
  const mPoll = useRef(null);
  useEffect(() => () => { if (mPoll.current) clearInterval(mPoll.current); }, []);
  const mirrorReady = Boolean(mBoard && mGrade && mSubject);
  const mScope = () => ({ board: mBoard, grade: mGrade, subject: mSubject });
  const resetMirror = () => { setMPrev(null); setMJob(null); setMConfirm(""); };

  const mirrorPreview = async () => {
    setMChecking(true); setMPrev(null); setMJob(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/content-mirror/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ content_scope: mScope() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || "Preview failed");
      setMPrev(data);
      toast[data.total_deletions ? "warning" : "info"](`${data.total_deletions} production record(s) would be deleted in this scope`);
    } catch (e) { toast.error(e.message || "Preview failed"); }
    setMChecking(false);
  };

  const mirrorRun = async () => {
    setMRunning(true); setMJob(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/content-mirror/execute`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ content_scope: mScope(), confirm: "MIRROR" }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || "Mirror failed to start");
      setMJob({ status: "running", phase: "planning" });
      toast.info("Mirroring to Production — deleting extras, then re-pushing preview…");
      mPoll.current = setInterval(async () => {
        try {
          const s = await (await fetch(`${API}/api/admin/migration/status/${data.job_id}`, { credentials: "include" })).json();
          setMJob(s.job);
          if (s.job.status === "completed" || s.job.status === "failed") {
            clearInterval(mPoll.current); setMRunning(false); setMConfirm(""); setMPrev(null);
            if (s.job.status === "completed") toast.success("Mirror complete — Production now matches preview for this scope");
            else toast.error("Mirror failed: " + (s.job.error || "unknown"));
          }
        } catch { /* keep polling */ }
      }, 1500);
    } catch (e) { toast.error(e.message || "Mirror failed to start"); setMRunning(false); }
  };

  // ---- Mirror ALL content (every board/class/subject) in one job ----
  const [maPrev, setMaPrev] = useState(null);
  const [maChecking, setMaChecking] = useState(false);
  const [maConfirm, setMaConfirm] = useState("");
  const [maRunning, setMaRunning] = useState(false);
  const [maJob, setMaJob] = useState(null);
  const maPoll = useRef(null);
  useEffect(() => () => { if (maPoll.current) clearInterval(maPoll.current); }, []);

  const mirrorAllPreview = async () => {
    setMaChecking(true); setMaPrev(null); setMaJob(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/content-mirror/preview-all`, {
        method: "POST", credentials: "include",
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || "Preview failed");
      setMaPrev(data);
      toast[data.total_deletions ? "warning" : "info"](`${data.total_deletions} production record(s) would be deleted across ALL content`);
    } catch (e) { toast.error(e.message || "Preview failed"); }
    setMaChecking(false);
  };

  const mirrorAllRun = async () => {
    setMaRunning(true); setMaJob(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/content-mirror/execute-all`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ confirm: "MIRROR ALL" }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || "Mirror failed to start");
      setMaJob({ status: "running", phase: "planning" });
      toast.info("Mirroring ALL content to Production…");
      maPoll.current = setInterval(async () => {
        try {
          const s = await (await fetch(`${API}/api/admin/migration/status/${data.job_id}`, { credentials: "include" })).json();
          setMaJob(s.job);
          if (s.job.status === "completed" || s.job.status === "failed") {
            clearInterval(maPoll.current); setMaRunning(false); setMaConfirm(""); setMaPrev(null);
            if (s.job.status === "completed") toast.success("Mirror ALL complete — Production now matches Preview");
            else toast.error("Mirror failed: " + (s.job.error || "unknown"));
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (e) { toast.error(e.message || "Mirror failed to start"); setMaRunning(false); }
  };

  // ---- Clear ALL curriculum content (Preview only) ----
  const [clearConfirm, setClearConfirm] = useState("");
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState(null);

  const clearContent = async () => {
    if (clearConfirm !== "CLEAR") return toast.error("Type CLEAR to confirm");
    if (!window.confirm("Wipe ALL curriculum content in Preview (topics, sub-concepts, questions, question bank, textbooks)?\n\nAccounts, subscriptions, payments, schools, settings and student test data are NOT touched.\n\nThis cannot be undone.")) return;
    setClearing(true); setClearResult(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/content/clear`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ confirm: "CLEAR" }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || "Clear failed");
      setClearResult(data); setClearConfirm("");
      toast.success(`Cleared ${data.total} curriculum records — Preview is now a blank slate`);
    } catch (e) { toast.error(e.message || "Clear failed"); }
    setClearing(false);
  };

  return (
    <Card className="border-red-300 bg-red-50/40" data-testid="danger-zone">
      <CardHeader className="cursor-pointer" onClick={toggle}>
        <CardTitle className="text-base flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-4 w-4" /> Danger zone — destructive expert actions
        </CardTitle>
        <CardDescription>Irreversible operations. Super admin only, each requires a typed confirmation.</CardDescription>
      </CardHeader>
      {open && (
        <CardContent className="space-y-6">
          {/* Mirror / Replace */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-red-800 flex items-center gap-1">
              <ArrowLeftRight className="h-4 w-4" /> Mirror / Replace — make Production EXACTLY match Preview
            </p>
            <p className="text-xs text-gray-600">
              Requires a specific <strong>board + class + subject</strong>. Deletes production topics, questions &amp; textbooks
              in this scope that aren't in preview, then re-pushes preview. Only touches curriculum content — never student/user data.
              Recorded in the Audit Logs tab.
            </p>
            <ScopePicker
              board={mBoard} grade={mGrade} subject={mSubject}
              setBoard={setMBoard} setGrade={setMGrade} setSubject={setMSubject}
              testidPrefix="mirror" onChange={resetMirror}
            />
            {!mirrorReady ? (
              <p className="text-xs text-amber-600" data-testid="mirror-scope-hint">Pick a specific board, class and subject to enable mirror.</p>
            ) : (
              <>
                <Button onClick={mirrorPreview} disabled={readOnly || mChecking || mRunning} variant="outline"
                  className="border-red-300 text-red-700" data-testid="mirror-preview-btn">
                  {mChecking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SearchCheck className="h-4 w-4 mr-2" />}
                  Preview deletions
                </Button>
                {mPrev && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm" data-testid="mirror-preview-result">
                    <p className="font-medium text-red-800 mb-1">{mPrev.total_deletions} record(s) would be DELETED from Production</p>
                    <div className="text-red-900 space-y-0.5">
                      <div>Topics: {mPrev.deletions.topics.count}{mPrev.deletions.topics.sample?.length ? ` — e.g. ${mPrev.deletions.topics.sample.slice(0, 6).join(", ")}` : ""}</div>
                      <div>Questions: {mPrev.deletions.questions.count}</div>
                      <div>Textbooks: {mPrev.deletions.textbooks.count}</div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">Then re-push from preview: {mPrev.upserts.topics} topics · {mPrev.upserts.questions} questions · {mPrev.upserts.textbooks} textbooks.</p>
                    <div className="flex items-center gap-2 mt-3">
                      <input value={mConfirm} onChange={(e) => setMConfirm(e.target.value)} placeholder='Type MIRROR to confirm'
                        className="border border-red-300 rounded-lg px-3 py-2 text-sm w-48" data-testid="mirror-confirm-input" />
                      <Button onClick={mirrorRun} disabled={readOnly || mRunning || mConfirm !== "MIRROR"}
                        className="bg-red-600 hover:bg-red-700 text-white" data-testid="mirror-run-btn">
                        {mRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowLeftRight className="h-4 w-4 mr-2" />}
                        Run mirror (replace)
                      </Button>
                    </div>
                  </div>
                )}
                {mJob && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm" data-testid="mirror-job-status">
                    <p className="font-medium text-red-800">
                      {mJob.status === "completed" ? "Mirror complete" : mJob.status === "failed" ? "Mirror failed" : `Mirroring… (${mJob.phase || "working"})`}
                    </p>
                    {mJob.deleted && <p className="text-red-900">Deleted — topics {mJob.deleted.topics}, questions {mJob.deleted.questions}, textbooks {mJob.deleted.textbooks}</p>}
                    {mJob.upserted && <p className="text-red-900">Re-pushed — topics {mJob.upserted.topics}, questions {mJob.upserted.questions}, textbooks {mJob.upserted.textbooks}</p>}
                    {mJob.error && <p className="text-red-600 text-xs mt-1">{mJob.error}</p>}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mirror ALL content */}
          <div className="space-y-2 pt-4 border-t border-red-200" data-testid="mirror-all-block">
            <p className="text-sm font-medium text-red-800 flex items-center gap-1">
              <ArrowLeftRight className="h-4 w-4" /> Mirror ALL content to Production (every board / class / subject)
            </p>
            <p className="text-xs text-gray-600">
              Makes Production <strong>exactly match Preview</strong> across all curriculum content in one run —
              deletes any production topics, questions &amp; textbooks not in Preview, then re-pushes Preview.
              Never touches student/user data. Recorded in the Audit Logs tab.
            </p>
            <Button onClick={mirrorAllPreview} disabled={maChecking || maRunning} variant="outline"
              className="border-red-300 text-red-700" data-testid="mirror-all-preview-btn">
              {maChecking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SearchCheck className="h-4 w-4 mr-2" />}
              Preview full mirror
            </Button>
            {maPrev && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm" data-testid="mirror-all-preview-result">
                <p className="font-medium text-red-800 mb-1">{maPrev.total_deletions} record(s) would be DELETED from Production (all content)</p>
                <div className="text-red-900 space-y-0.5">
                  <div>Topics: {maPrev.deletions.topics.count}</div>
                  <div>Questions: {maPrev.deletions.questions.count}</div>
                  <div>Textbooks: {maPrev.deletions.textbooks.count}</div>
                </div>
                <p className="text-xs text-gray-600 mt-2">Then re-push from Preview: {maPrev.upserts.topics} topics · {maPrev.upserts.questions} questions · {maPrev.upserts.textbooks} textbooks.</p>
                <div className="flex items-center gap-2 mt-3">
                  <input value={maConfirm} onChange={(e) => setMaConfirm(e.target.value)} placeholder='Type MIRROR ALL to confirm'
                    className="border border-red-300 rounded-lg px-3 py-2 text-sm w-52" data-testid="mirror-all-confirm-input" />
                  <Button onClick={mirrorAllRun} disabled={maRunning || maConfirm !== "MIRROR ALL"}
                    className="bg-red-600 hover:bg-red-700 text-white" data-testid="mirror-all-run-btn">
                    {maRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowLeftRight className="h-4 w-4 mr-2" />}
                    Run full mirror
                  </Button>
                </div>
              </div>
            )}
            {maJob && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm" data-testid="mirror-all-job-status">
                <p className="font-medium text-red-800">
                  {maJob.status === "completed" ? "Mirror ALL complete" : maJob.status === "failed" ? "Mirror failed" : `Mirroring all content… (${maJob.phase || "working"})`}
                </p>
                {maJob.deleted && <p className="text-red-900">Deleted — topics {maJob.deleted.topics}, questions {maJob.deleted.questions}, textbooks {maJob.deleted.textbooks}</p>}
                {maJob.upserted && <p className="text-red-900">Re-pushed — topics {maJob.upserted.topics}, questions {maJob.upserted.questions}, textbooks {maJob.upserted.textbooks}</p>}
                {maJob.error && <p className="text-red-600 text-xs mt-1">{maJob.error}</p>}
              </div>
            )}
          </div>

          {/* Clear all curriculum content (Preview only) */}
          <div className="space-y-2 pt-4 border-t border-red-200" data-testid="clear-content-block">
            <p className="text-sm font-medium text-red-800 flex items-center gap-1">
              <Trash2 className="h-4 w-4" /> Clear all curriculum content — start from scratch (Preview only)
            </p>
            <p className="text-xs text-gray-600">
              Deletes <strong>only</strong> curriculum content: topics, sub-concepts, practice questions, the question bank,
              textbooks and AI-generated topics. It does <strong>not</strong> touch admin/parent/student accounts, subscriptions,
              payments, schools, settings, or student test data (mastery, answers, assessments). Recorded in the Audit Logs tab.
            </p>
            {readOnly ? (
              <p className="text-xs text-amber-600" data-testid="clear-content-prod-note">This action is disabled in Production. Clear content in Preview, then use Mirror to update Production.</p>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <input value={clearConfirm} onChange={(e) => setClearConfirm(e.target.value)} placeholder="Type CLEAR to confirm"
                    className="border border-red-300 rounded-lg px-3 py-2 text-sm w-48" data-testid="clear-content-confirm-input" />
                  <Button onClick={clearContent} disabled={clearing || clearConfirm !== "CLEAR"}
                    className="bg-red-600 hover:bg-red-700 text-white" data-testid="clear-content-btn">
                    {clearing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Clear all content
                  </Button>
                </div>
                {clearResult && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm" data-testid="clear-content-result">
                    <p className="font-medium text-red-800">Cleared {clearResult.total} records</p>
                    <div className="text-red-900 text-xs mt-1 space-y-0.5">
                      {Object.entries(clearResult.deleted || {}).map(([k, v]) => <div key={k}>{k}: {v}</div>)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Drop database */}
          <div className="space-y-2 pt-4 border-t border-red-200">
            <p className="text-sm font-medium text-red-800">Drop an unused database</p>
            <p className="text-xs text-gray-600">Permanently deletes an entire database and all its collections. The active database cannot be selected.</p>
            <div className="flex flex-wrap items-center gap-2">
              <select className="h-10 rounded-md border border-red-300 bg-white px-3 text-sm" value={target}
                onChange={(e) => { setTarget(e.target.value); setConfirm(""); }} data-testid="danger-db-select">
                <option value="">Select a database…</option>
                {dbs.map((d) => (
                  <option key={d.name} value={d.name}>{d.name} ({d.collections_count} collections)</option>
                ))}
              </select>
              {target && (
                <>
                  <input className="h-10 rounded-md border border-red-300 px-3 text-sm w-56"
                    placeholder={`Type DELETE_${target.toUpperCase()}`} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} data-testid="danger-db-confirm" />
                  <Button onClick={drop} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="danger-db-drop-btn">
                    {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
                    Drop database
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function DataMigrationTab({ readOnly = false }) {
  const [config, setConfig] = useState(null);
  const [direction, setDirection] = useState("push"); // push = preview->prod, pull = prod->preview
  const [selected, setSelected] = useState([]);
  const [preset, setPreset] = useState("");
  const [strategy, setStrategy] = useState("merge");
  const [dryRows, setDryRows] = useState(null);
  const [dryLoading, setDryLoading] = useState(false);
  const [diffRows, setDiffRows] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [missingOnly, setMissingOnly] = useState(false);
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/migration/config`, { credentials: "include" });
        const data = await res.json();
        if (data.success) {
          setConfig(data);
          setSelected(data.collections);
        } else {
          toast.error(data.detail || "Failed to load migration config");
        }
      } catch (e) {
        toast.error("Failed to load migration config");
      }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const toggleColl = (c) => {
    setSelected((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
    setDryRows(null);
  };

  const selectAll = () => { setSelected(config.collections); setDryRows(null); };
  const selectNone = () => { setSelected([]); setDryRows(null); };

  const body = () => ({
    direction,
    collections: selected,
    strategy,
    missing_only: missingOnly,
    ...(preset ? { filter_preset: preset } : {}),
  });

  const runDetect = async () => {
    if (selected.length === 0) return toast.error("Select at least one collection");
    setDiffLoading(true);
    setDiffRows(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/diff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body()),
      });
      const data = await res.json();
      if (data.success) {
        setDiffRows(data.rows);
        const withMissing = data.rows.filter((r) => (r.missing_count || 0) > 0).map((r) => r.collection);
        if (withMissing.length > 0) {
          setSelected(withMissing);
          setMissingOnly(true);
          toast.success(`Found records missing in target across ${withMissing.length} collection(s). Pre-selected them — only absent records will migrate.`);
        } else {
          toast.info("Target is already up to date — no missing records found.");
        }
      } else {
        toast.error(data.detail || "Detect failed");
      }
    } catch (e) {
      toast.error("Detect failed: " + e.message);
    }
    setDiffLoading(false);
  };

  const runDryRun = async () => {
    if (selected.length === 0) return toast.error("Select at least one collection");
    setDryLoading(true);
    setDryRows(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/dry-run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body()),
      });
      const data = await res.json();
      if (data.success) setDryRows(data.rows);
      else toast.error(data.detail || "Dry-run failed");
    } catch (e) {
      toast.error("Dry-run failed: " + e.message);
    }
    setDryLoading(false);
  };

  const pollStatus = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/admin/migration/status/${jobId}`, { credentials: "include" });
        const data = await res.json();
        if (data.success) {
          setJob(data.job);
          if (data.job.status === "completed" || data.job.status === "failed") {
            clearInterval(pollRef.current);
            setRunning(false);
            if (data.job.status === "completed") toast.success("Migration completed");
            else toast.error("Migration failed: " + (data.job.error || "unknown"));
          }
        }
      } catch (e) { /* keep polling */ }
    }, 1500);
  };

  const startMigration = async () => {
    if (selected.length === 0) return toast.error("Select at least one collection");
    const local = `this env (${config.current_db})`;
    const remote = config.remote_url ? config.remote_url.replace(/^https?:\/\//, "") : "remote";
    const dirLabel = direction === "push" ? `${local} → ${remote}` : `${remote} → ${local}`;
    const modeLine = missingOnly
      ? "Mode: SAFE CATCH-UP — only records missing in target are added. Existing users untouched."
      : `Strategy: ${strategy} (add + update, never delete)`;
    if (!window.confirm(`Start migration ${dirLabel}?\n\n${modeLine}\nCollections: ${selected.length}\n\nProceed?`)) return;
    setRunning(true);
    setJob(null);
    try {
      const res = await fetch(`${API}/api/admin/migration/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body()),
      });
      const data = await res.json();
      if (data.success) { pollStatus(data.job_id); }
      else { toast.error(data.detail || "Failed to start migration"); setRunning(false); }
    } catch (e) {
      toast.error("Failed to start: " + e.message);
      setRunning(false);
    }
  };

  if (!config) {
    return <div className="flex items-center gap-2 text-gray-500 p-6" data-testid="migration-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading migration utility…
    </div>;
  }

  const pct = job && job.total > 0 ? Math.min(100, Math.round((job.processed / job.total) * 100)) : (job && job.status === "completed" ? 100 : 0);

  const localLabel = `This env (${config.current_db})`;
  const remoteLabel = config.remote_url ? `Remote (${config.remote_url.replace(/^https?:\/\//, "")})` : "Remote (not set)";
  const sourceLabel = direction === "push" ? localLabel : remoteLabel;
  const targetLabel = direction === "push" ? remoteLabel : localLabel;

  return (
    <div className="space-y-4" data-testid="data-migration-tab">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-indigo-100"><DatabaseZap className="h-5 w-5 text-indigo-600" /></div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Data Migration</h2>
          <p className="text-sm text-gray-500">Promote content from <strong>Preview → Production</strong>. Every promotion and mirror is recorded in the <strong>Audit Logs</strong> tab.</p>
        </div>
      </div>

      {/* PRIMARY FLOW */}
      <div data-testid="promote-primary">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-emerald-600">
            <PlayCircle className="h-3.5 w-3.5" /> Everyday · Preview → Production
          </span>
        </div>
        <PromoteCard readOnly={readOnly} />
      </div>

      {/* ADVANCED TOOLS (collapsed) */}
      <details className="rounded-lg border border-gray-200 bg-white" data-testid="advanced-tools">
        <summary className="cursor-pointer px-4 py-3 font-medium text-gray-700 select-none">
          Advanced tools — collection-level migration &amp; pull from Production (power users)
        </summary>
        <div className="p-4 space-y-4">

      {/* Environment status */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-4 text-sm">
          <span className="flex items-center gap-2"><ServerCog className="h-4 w-4 text-gray-400" />
            Current DB: <Badge variant="outline" data-testid="migration-current-db">{config.current_db}</Badge></span>
          <span className="flex items-center gap-2">Remote:
            {config.remote_configured
              ? <Badge className="bg-emerald-100 text-emerald-700" data-testid="migration-remote-ok">{config.remote_url}</Badge>
              : <Badge className="bg-amber-100 text-amber-700" data-testid="migration-remote-missing">Not configured — set MIGRATION_SECRET_PROD</Badge>}
          </span>
        </CardContent>
      </Card>

      {!config.remote_configured && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="migration-remote-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>Remote connection isn't ready. Set <code>MIGRATION_SECRET_PROD</code> (the production secret) and <code>PROD_BACKEND_URL</code> in this environment's secrets, then reload.</span>
        </div>
      )}

      {/* Controls */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="text-base">Configure Migration</CardTitle>
          <CardDescription>Pick direction, collections and strategy. Run a dry-run first.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Direction */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Direction</p>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => { setDirection("push"); setDryRows(null); setDiffRows(null); }}
                data-testid="migration-direction-push"
                className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition-colors ${direction === "push" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-200"}`}>
                {localLabel} <ArrowRight className="h-4 w-4" /> {remoteLabel}
              </button>
              <button onClick={() => { setDirection("pull"); setDryRows(null); setDiffRows(null); }}
                data-testid="migration-direction-pull"
                className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition-colors ${direction === "pull" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-200"}`}>
                {remoteLabel} <ArrowRight className="h-4 w-4" /> {localLabel}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              <strong>Source</strong> is always this environment's DB; <strong>Target</strong> is the remote it connects to. Run migrations from <strong>Preview only</strong> — running from Production points it back at itself.
            </p>
          </div>

          {/* Filter preset & strategy */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Filter (optional)</p>
              <select value={preset} onChange={(e) => { setPreset(e.target.value); setDryRows(null); }}
                data-testid="migration-filter-preset"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <option value="">All documents</option>
                {config.filter_presets.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Strategy</p>
              <select value={strategy} onChange={(e) => setStrategy(e.target.value)}
                data-testid="migration-strategy"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <option value="merge">Merge / Upsert (add + update, never delete)</option>
                <option value="overwrite">Overwrite existing (replace matched docs)</option>
              </select>
            </div>
          </div>

          {/* Collections */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Collections ({selected.length}/{config.collections.length})</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll} data-testid="migration-select-all">All</Button>
                <Button variant="outline" size="sm" onClick={selectNone} data-testid="migration-select-none">None</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto p-2 border border-gray-100 rounded-lg">
              {config.collections.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid={`migration-coll-${c}`}>
                  <input type="checkbox" checked={selected.includes(c)} onChange={() => toggleColl(c)} className="rounded" />
                  <span className="truncate">{c}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Missing-only safe catch-up */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-200">
            <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)}
              data-testid="migration-missing-only" className="mt-0.5 rounded" />
            <label className="text-sm text-indigo-900 cursor-pointer" onClick={() => setMissingOnly(!missingOnly)}>
              <span className="font-medium">Safe catch-up: migrate only records missing in the target</span>
              <span className="block text-indigo-700/80">Existing users are never touched — no PIN/password/flags overwritten. Use "Detect missing" to auto-find and pre-select them.</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={runDetect} disabled={readOnly || diffLoading || running || !config.remote_configured}
              variant="outline" className="border-indigo-300 text-indigo-700" data-testid="migration-detect-btn">
              {diffLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SearchCheck className="h-4 w-4 mr-2" />}
              Detect missing in target
            </Button>
            <Button onClick={runDryRun} disabled={readOnly || dryLoading || running || !config.remote_configured}
              variant="outline" data-testid="migration-dry-run-btn">
              {dryLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Dry Run (preview counts)
            </Button>
            <Button onClick={startMigration} disabled={readOnly || running || !config.remote_configured}
              className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="migration-start-btn">
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
              {missingOnly ? "Migrate Missing Only" : "Start Migration"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dry-run results */}
      {dryRows && (
        <Card className="bg-white border-gray-200" data-testid="migration-dryrun-results">
          <CardHeader><CardTitle className="text-base">Dry Run — {sourceLabel} → {targetLabel}</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Collection</th><th className="py-2">Source</th><th className="py-2">Target</th><th className="py-2">Δ</th>
                </tr></thead>
                <tbody>
                  {dryRows.map((r) => (
                    <tr key={r.collection} className="border-b last:border-0">
                      <td className="py-1.5 font-medium text-gray-800">{r.collection}</td>
                      {r.error
                        ? <td colSpan={3} className="py-1.5 text-red-500">{r.error}</td>
                        : <>
                            <td className="py-1.5">{r.source_count}</td>
                            <td className="py-1.5">{r.target_count}</td>
                            <td className="py-1.5">{r.source_count - r.target_count > 0 ? `+${r.source_count - r.target_count}` : 0}</td>
                          </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detect-missing results */}
      {diffRows && (
        <Card className="bg-white border-gray-200" data-testid="migration-diff-results">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><SearchCheck className="h-4 w-4 text-indigo-500" /> Missing in Target — {targetLabel}</CardTitle>
            <CardDescription>Records present in the source but absent in the target (matched by identity key). Only these will migrate in safe catch-up mode.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Collection</th><th className="py-2">Key</th><th className="py-2">Source</th><th className="py-2">Target</th><th className="py-2">Missing</th><th className="py-2">Examples</th>
                </tr></thead>
                <tbody>
                  {diffRows.map((r) => (
                    <tr key={r.collection} className="border-b last:border-0" data-testid={`migration-diff-row-${r.collection}`}>
                      <td className="py-1.5 font-medium text-gray-800">{r.collection}</td>
                      {r.error
                        ? <td colSpan={5} className="py-1.5 text-red-500">{r.error}</td>
                        : <>
                            <td className="py-1.5 text-gray-500">{r.identity_field}</td>
                            <td className="py-1.5">{r.source_count}</td>
                            <td className="py-1.5">{r.target_count}</td>
                            <td className="py-1.5"><span className={r.missing_count > 0 ? "text-amber-600 font-semibold" : "text-gray-400"}>{r.missing_count}</span></td>
                            <td className="py-1.5 text-gray-500 truncate max-w-[200px]">{(r.sample_missing || []).join(", ") || "—"}</td>
                          </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

        </div>
      </details>

      {/* Advanced job progress */}
      {job && (
        <Card className="bg-white border-gray-200" data-testid="migration-job-progress">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {job.status === "completed" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
              {job.status === "failed" && <XCircle className="h-4 w-4 text-red-500" />}
              {(job.status === "running" || job.status === "queued") && <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />}
              Migration {job.status}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div className="bg-indigo-600 h-2.5 rounded-full transition-all" style={{ width: `${pct}%` }} data-testid="migration-progress-bar" />
            </div>
            <p className="text-sm text-gray-600" data-testid="migration-progress-text">{job.processed} / {job.total} documents ({pct}%)</p>
            {job.error && <p className="text-sm text-red-500">{job.error}</p>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Collection</th><th className="py-2">Processed</th><th className="py-2">Inserted</th><th className="py-2">Updated</th><th className="py-2">Unchanged</th><th className="py-2">Errors</th>
                </tr></thead>
                <tbody>
                  {Object.entries(job.collections || {}).map(([c, s]) => (
                    <tr key={c} className="border-b last:border-0">
                      <td className="py-1.5 font-medium text-gray-800">{c}</td>
                      <td className="py-1.5">{s.processed}/{s.total}</td>
                      <td className="py-1.5 text-emerald-600">{s.inserted}</td>
                      <td className="py-1.5 text-blue-600">{s.updated}</td>
                      <td className="py-1.5 text-gray-400">{s.unchanged}</td>
                      <td className="py-1.5 text-red-500">{s.errors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <DangerZone readOnly={readOnly} />
    </div>
  );
}
